import React, { useState, useEffect } from "react"
import { useLocation } from "react-router-dom"
import Swal from "sweetalert2"
import {
  Alert,
  Button,
  Container,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap"
import storage from "services/storage"
import { authUserKey } from "config/API"
import { Loading } from "components"

import { useMutation, useQuery } from "@tanstack/react-query"
import ListHeader from "ui-componets/list-header"
import ListFilterBar from "ui-componets/list-filter-bar"
import { getDailyCashCuts, getManualCashCuts, getMonthlyCashCuts, getWeeklyCashCuts } from "services/cash-cuts"
import { createPreviousMonthCashCut } from "services/periodic-tasks"
import { getBusinessInfo } from "services/business-info"
import CashCutItemList from "components/sales/cashCut-item-list"
import CashCutDetailsModal from "components/sales/CashCutDetailsModal"
import { CashCutTicketPrinter } from "components/pointy-printer-js"
import TablePaginationFooter from "components/TablePaginationFooter"
import { getDateFilterParams } from "utils/date-filters"
import "assets/scss/custom/components/table-interactive.scss"

const PAGE_SIZE = 30

const normalizeFilterValues = (value) => {
  if (Array.isArray(value)) {
    return value
  }

  if (!value || value === "all") {
    return []
  }

  return [value]
}

const getCashCutTypeParams = (typeValue, fallbackPrefix) => {
  const selectedTypes = normalizeFilterValues(typeValue)

  if (selectedTypes.length === 1) {
    return { type_cash_cut: selectedTypes[0] }
  }

  if (selectedTypes.length > 1) {
    return { type_cash_cut__in: selectedTypes.join(",") }
  }

  return { type_cash_cut__icontains: fallbackPrefix }
}

const CashCutList = () => {
  document.title = "Cortes de Caja | Punto de venta"
  const Location = useLocation()
  const [filters, setFilters] = useState("")
  const [searchTxt, setSearchTxt] = useState(new URLSearchParams(Location.search).get("search") ?? "")
  const [searchTerm, setSearchTerm] = useState(new URLSearchParams(Location.search).get("search") ?? "")
  const [dailyPage, setDailyPage] = useState(1)
  const [manualPage, setManualPage] = useState(1)
  const [monthlyPage, setMonthlyPage] = useState(1)
  const [weeklyPage, setWeeklyPage] = useState(1)
  const [tabState, settabState] = useState({ activeTab: "manual" })
  const [dailyType, setDailyType] = useState("Corte de caja diario A")
  const [manualType, setManualType] = useState("Corte de caja manual A")
  const [monthlyType, setMonthlyType] = useState("Corte de caja mensual A")
  const [weeklyType, setWeeklyType] = useState("Corte de caja semanal A")
  const [dateFilter, setDateFilter] = useState({ preset: "", startDate: "", endDate: "" })
  const [selectedCashCut, setSelectedCashCut] = useState(null)
  const [detailsModalOpen, setDetailsModalOpen] = useState(false)
  const [cashCutToPrint, setCashCutToPrint] = useState({ isReady: false, item: null })
  const permissions = storage.get(authUserKey).user_permissions || []
  const canAddCashCut = permissions.some((e) => e.codename === "add_cashcut")

  const dailyQueryFilters = {
    offset: (dailyPage - 1) * PAGE_SIZE,
    per_page: PAGE_SIZE,
    ordering: "-date",
    ...(searchTerm ? { search: searchTerm } : {}),
    ...getDateFilterParams(dateFilter, "date"),
    ...getCashCutTypeParams(dailyType, "Corte de caja diario"),
  }

  const manualQueryFilters = {
    offset: (manualPage - 1) * PAGE_SIZE,
    per_page: PAGE_SIZE,
    ordering: "-date",
    ...(searchTerm ? { search: searchTerm } : {}),
    ...getDateFilterParams(dateFilter, "date"),
    ...getCashCutTypeParams(manualType, "Corte de caja manual"),
  }

  const monthlyQueryFilters = {
    offset: (monthlyPage - 1) * PAGE_SIZE,
    per_page: PAGE_SIZE,
    ordering: "-date",
    ...(searchTerm ? { search: searchTerm } : {}),
    ...getDateFilterParams(dateFilter, "date"),
    ...getCashCutTypeParams(monthlyType, "Corte de caja mensual"),
  }

  const weeklyQueryFilters = {
    offset: (weeklyPage - 1) * PAGE_SIZE,
    per_page: PAGE_SIZE,
    ordering: "-date",
    ...(searchTerm ? { search: searchTerm } : {}),
    ...getDateFilterParams(dateFilter, "date"),
    ...getCashCutTypeParams(weeklyType, "Corte de caja semanal"),
  }

  const cashCuts = useQuery(["cash-cuts-daily", dailyQueryFilters], () => getDailyCashCuts(dailyQueryFilters), {
    keepPreviousData: true,
  })
  const manualCashCuts = useQuery(["cash-cuts-manual", manualQueryFilters], () => getManualCashCuts(manualQueryFilters), {
    keepPreviousData: true,
  })
  const monthlyCashCuts = useQuery(["cash-cuts-monthly", monthlyQueryFilters], () => getMonthlyCashCuts(monthlyQueryFilters), {
    keepPreviousData: true,
  })
  const weeklyCashCuts = useQuery(["cash-cuts-weekly", weeklyQueryFilters], () => getWeeklyCashCuts(weeklyQueryFilters), {
    keepPreviousData: true,
  })
  const businessInfo = useQuery(["business-info"], () => getBusinessInfo())
  const styleCenterTable = { textAlign: "center", verticalAlign: "middle" }
  const previousMonthCashCutMutation = useMutation(() => createPreviousMonthCashCut())

  const dailyTypeOptions = [
    { value: "all", label: "Todos" },
    { value: "Corte de caja diario A", label: "Tickets" },
    { value: "Corte de caja diario B", label: "Ventas pendientes" },
    { value: "Corte de caja diario Total", label: "Ambas (Tickets + Ventas Pendientes)" },
  ]

  const manualTypeOptions = [
    { value: "all", label: "Todos" },
    { value: "Corte de caja manual A", label: "Tickets" },
    { value: "Corte de caja manual B", label: "Ventas pendientes" },
    { value: "Corte de caja manual Total", label: "Ambas (Tickets + Ventas Pendientes)" },
  ]

  const monthlyTypeOptions = [
    { value: "all", label: "Todos" },
    { value: "Corte de caja mensual A", label: "Tickets" },
    { value: "Corte de caja mensual B", label: "Ventas pendientes" },
    { value: "Corte de caja mensual Total", label: "Ambas (Tickets + Ventas Pendientes)" },
  ]

  const weeklyTypeOptions = [
    { value: "all", label: "Todos" },
    { value: "Corte de caja semanal A", label: "Tickets" },
    { value: "Corte de caja semanal B", label: "Ventas pendientes" },
    { value: "Corte de caja semanal Total", label: "Ambas (Tickets + Ventas Pendientes)" },
  ]

  const handleDailyTypeChange = (type) => {
    setDailyType(type)
    setDailyPage(1)
  }

  const handleManualTypeChange = (type) => {
    setManualType(type)
    setManualPage(1)
  }

  const handleMonthlyTypeChange = (type) => {
    setMonthlyType(type)
    setMonthlyPage(1)
  }

  const handleWeeklyTypeChange = (type) => {
    setWeeklyType(type)
    setWeeklyPage(1)
  }

  const handleCreatePreviousMonthCashCut = async () => {
    const result = await Swal.fire({
      title: "Corte de Caja Mensual",
      text: "Se generara el corte del mes anterior, si ya existe, no se creara de nuevo.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#6fc33a",
      cancelButtonColor: "#d33",
      confirmButtonText: "Crear corte",
      cancelButtonText: "Cancelar",
    })

    if (!result.isConfirmed) return

    try {
      await previousMonthCashCutMutation.mutateAsync()
      await monthlyCashCuts.refetch()
      Swal.fire({
        title: "Corte creado",
        text: "El corte de caja del mes anterior se creo correctamente.",
        icon: "success",
        confirmButtonColor: "#6fc33a",
      })
    } catch (error) {
      const message =
        error?.errors?.map((item) => item?.message).filter(Boolean).join(" ") ||
        error?.message ||
        "No se pudo crear el corte de caja del mes anterior, intenta de nuevo mas tarde."
      Swal.fire({
        title: "No se pudo crear",
        text: message,
        icon: "error",
        confirmButtonColor: "#6fc33a",
      })
    }
  }

  const handleBeforePrint = (item) => {
    setSelectedCashCut({ ...item })
    setCashCutToPrint({ isReady: true, item: { ...item } })
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setDailyPage(1)
      setManualPage(1)
      setMonthlyPage(1)
      setWeeklyPage(1)
      setSearchTerm(searchTxt.trim())
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTxt])

  const handleAfterPrint = () => {
    setCashCutToPrint((prevState) => ({ ...prevState, isReady: false }))
  }

  const openDetails = (item) => {
    setSelectedCashCut(item)
    setDetailsModalOpen(true)
  }

  const closeDetails = () => {
    setDetailsModalOpen(false)
  }

  const handleSearch = (pageParams) => {
    const params = new URLSearchParams(filters)
    if (pageParams) {
      const newParams = new URLSearchParams(pageParams)
      window.history.replaceState({}, null, `${Location.pathname}?${newParams}`)
      setFilters(newParams)
      return
    }

    const trimmedSearch = searchTxt.trim()
    setSearchTerm(trimmedSearch)
    params.delete("offset")
    params.delete("per_page")
    if (trimmedSearch) {
      params.set("search", trimmedSearch)
    } else {
      params.delete("search")
    }
    window.history.replaceState({}, null, `${Location.pathname}?${params}`)
    setFilters(params)
  }

  function handleChangeSearch(event) {
    setSearchTxt(event.target.value)
  }

  const handleDateFilterChange = (nextDateFilter) => {
    setDailyPage(1)
    setManualPage(1)
    setMonthlyPage(1)
    setWeeklyPage(1)
    setDateFilter(nextDateFilter)
  }

  const clearListFilters = () => {
    setDailyPage(1)
    setManualPage(1)
    setMonthlyPage(1)
    setWeeklyPage(1)
    setDateFilter({ preset: "", startDate: "", endDate: "" })
    setDailyType("Corte de caja diario A")
    setManualType("Corte de caja manual A")
    setMonthlyType("Corte de caja mensual A")
    setWeeklyType("Corte de caja semanal A")
  }

  const handlePageChange = (setter, nextPage, totalPages) => {
    if (nextPage < 1) return
    if (totalPages > 0 && nextPage > totalPages) return
    setter(nextPage)
  }

  const toggleActiveTab = (tab) => {
    if (tabState.activeTab !== tab) {
      settabState({
        activeTab: tab,
      })
    }
  }

  return !permissions.some((e) => e.codename === "view_cashcut") ? (
    <div className="page-content">
      <div className="text-center">
        <h1>No autorizado para ver esta pagina</h1>
      </div>
    </div>
  ) : (
    <div className="page-content">
      <Container fluid>
        <Alert color="danger" isOpen={cashCuts.isError}>
          {cashCuts.error?.errors?.map((error) => error?.message)}
        </Alert>

        <div className="d-flex align-items-center justify-content-between">
          <h2>Lista de Cortes de Caja</h2>
          {tabState.activeTab === "mensual" && canAddCashCut && (
            <Button
              color="primary"
              className="d-flex align-items-center mb-2"
              disabled={previousMonthCashCutMutation.isLoading}
              onClick={handleCreatePreviousMonthCashCut}
            >
              <i className="me-2 fa fa-calendar-check" />
              <span className="text-truncate">Corte de caja mensual</span>
            </Button>
          )}
        </div>
        <div className="bg-white p-3">
          <Nav tabs>
            <NavItem>
              <NavLink
                className={` ${tabState.activeTab === "manual" && "active"}`}
                onClick={() => toggleActiveTab("manual")}
              >
                Manual
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={` ${tabState.activeTab === "diario" && "active"}`}
                onClick={() => toggleActiveTab("diario")}
              >
                Diario
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={` ${tabState.activeTab === "semanal" && "active"}`}
                onClick={() => toggleActiveTab("semanal")}
              >
                Semanal
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={` ${tabState.activeTab === "mensual" && "active"}`}
                onClick={() => toggleActiveTab("mensual")}
              >
                Mensual
              </NavLink>
            </NavItem>
          </Nav>
          <TabContent activeTab={tabState.activeTab}>
            <TabPane tabId="manual">
              <div style={{ marginTop: "20px", marginBottom: "20px" }}></div>
              <ListHeader
                handleOnChangeSearch={handleChangeSearch}
                handleSearch={handleSearch}
                searchTxt={searchTxt}
                filtersContent={
                  <div className="cash-cut-filters">
                    <div className="cash-cut-filters-date">
                      <ListFilterBar
                        dateFilter={dateFilter}
                        onDateFilterChange={handleDateFilterChange}
                      />
                    </div>
                    <div className="cash-cut-filters-actions">
                      <ListFilterBar
                        selects={[
                          {
                            name: "manualType",
                            label: "Tipo de corte",
                            icon: "fa fa-cash-register",
                            value: manualType,
                            onChange: handleManualTypeChange,
                            options: manualTypeOptions,
                          },
                        ]}
                        onClear={clearListFilters}
                      />
                    </div>
                  </div>
                }
              />
              {!manualCashCuts.error && manualCashCuts.isLoading ? (
                <div className="my-5 text-center">
                  <Loading isLoading={manualCashCuts.isLoading} size={2} />
                </div>
              ) : manualCashCuts.error && !manualCashCuts.isLoading ? (
                <div className="my-5 text-center">
                  <h1>Error intenta mas tarde</h1>
                </div>
              ) : (
                <div style={{ marginTop: "30px" }} className="table-responsive">
                  <Table className="table-hover-effect table-interactive">
                    <thead>
                      <tr>
                        <th style={styleCenterTable} className="text-cell">Tipo</th>
                        <th style={styleCenterTable} className="text-cell">Fecha</th>
                        <th style={styleCenterTable} className="money-cell">Ingresos Totales</th>
                        <th style={styleCenterTable} className="money-cell">Ingresos Facturados</th>
                        <th style={styleCenterTable} className="money-cell">Diferencia Total-Facturado</th>
                        <th style={styleCenterTable} className="text-cell">Usuario</th>
                        <th style={styleCenterTable} className="text-center">Detalles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {manualCashCuts.data?.results?.length > 0 ? (
                        manualCashCuts.data.results.map((item) => (
                          <CashCutItemList key={item.id} item={item} onView={openDetails} />
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center">
                            No hay datos
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                  <TablePaginationFooter
                    totalCount={manualCashCuts.data?.count || 0}
                    currentPage={manualPage}
                    pageSize={PAGE_SIZE}
                    itemLabel="cortes"
                    isLoading={manualCashCuts.isLoading}
                    isFetching={manualCashCuts.isFetching}
                    onChangePage={(nextPage) => handlePageChange(setManualPage, nextPage, Math.ceil((manualCashCuts.data?.count || 0) / PAGE_SIZE))}
                  />
                </div>
              )}
            </TabPane>
            <TabPane tabId="diario">
              <div style={{ marginTop: "20px", marginBottom: "20px" }}></div>
              <ListHeader
                handleOnChangeSearch={handleChangeSearch}
                handleSearch={handleSearch}
                searchTxt={searchTxt}
                filtersContent={
                  <div className="cash-cut-filters">
                    <div className="cash-cut-filters-date">
                      <ListFilterBar
                        dateFilter={dateFilter}
                        onDateFilterChange={handleDateFilterChange}
                      />
                    </div>
                    <div className="cash-cut-filters-actions">
                      <ListFilterBar
                        selects={[
                          {
                            name: "dailyType",
                            label: "Tipo de corte",
                            icon: "fa fa-cash-register",
                            value: dailyType,
                            onChange: handleDailyTypeChange,
                            options: dailyTypeOptions,
                          },
                        ]}
                        onClear={clearListFilters}
                      />
                    </div>
                  </div>
                }
              />
              {!cashCuts.error && cashCuts.isLoading ? (
                <div className="my-5 text-center">
                  <Loading isLoading={cashCuts.isLoading} size={2} />
                </div>
              ) : cashCuts.error && !cashCuts.isLoading ? (
                <div className="my-5 text-center">
                  <h1>Error intenta mas tarde</h1>
                </div>
              ) : (
                <div style={{ marginTop: "30px" }} className="table-responsive">
                  <Table className="table-hover-effect table-interactive">
                    <thead>
                      <tr>
                        <th style={styleCenterTable} className="text-cell">Tipo</th>
                        <th style={styleCenterTable} className="text-cell">Fecha</th>
                        <th style={styleCenterTable} className="money-cell">Ingresos Totales</th>
                        <th style={styleCenterTable} className="money-cell">Ingresos Facturados</th>
                        <th style={styleCenterTable} className="money-cell">Diferencia Total-Facturado</th>
                        <th style={styleCenterTable} className="text-center">Detalles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cashCuts.data?.results?.length > 0 ? (
                        cashCuts.data.results.map((item) => (
                          <CashCutItemList key={item.id} item={item} variant="daily" onView={openDetails} />
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="text-center">
                            No hay datos
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                  <TablePaginationFooter
                    totalCount={cashCuts.data?.count || 0}
                    currentPage={dailyPage}
                    pageSize={PAGE_SIZE}
                    itemLabel="cortes"
                    isLoading={cashCuts.isLoading}
                    isFetching={cashCuts.isFetching}
                    onChangePage={(nextPage) => handlePageChange(setDailyPage, nextPage, Math.ceil((cashCuts.data?.count || 0) / PAGE_SIZE))}
                  />
                </div>
              )}
            </TabPane>
            <TabPane tabId="semanal">
              <div style={{ marginTop: "20px", marginBottom: "20px" }}></div>
              <ListHeader
                handleOnChangeSearch={handleChangeSearch}
                handleSearch={handleSearch}
                searchTxt={searchTxt}
                filtersContent={
                  <div className="cash-cut-filters">
                    <div className="cash-cut-filters-date">
                      <ListFilterBar
                        dateFilter={dateFilter}
                        onDateFilterChange={handleDateFilterChange}
                      />
                    </div>
                    <div className="cash-cut-filters-actions">
                      <ListFilterBar
                        selects={[
                          {
                            name: "weeklyType",
                            label: "Tipo de corte",
                            icon: "fa fa-cash-register",
                            value: weeklyType,
                            onChange: handleWeeklyTypeChange,
                            options: weeklyTypeOptions,
                          },
                        ]}
                        onClear={clearListFilters}
                      />
                    </div>
                  </div>
                }
              />
              {!weeklyCashCuts.error && weeklyCashCuts.isLoading ? (
                <div className="my-5 text-center">
                  <Loading isLoading={weeklyCashCuts.isLoading} size={2} />
                </div>
              ) : weeklyCashCuts.error && !weeklyCashCuts.isLoading ? (
                <div className="my-5 text-center">
                  <h1>Error intenta mas tarde</h1>
                </div>
              ) : (
                <div style={{ marginTop: "30px" }} className="table-responsive">
                  <Table className="table-hover-effect table-interactive">
                    <thead>
                      <tr>
                        <th style={styleCenterTable} className="text-cell">Tipo</th>
                        <th style={styleCenterTable} className="text-cell">Fecha</th>
                        <th style={styleCenterTable} className="money-cell">Ingresos Totales</th>
                        <th style={styleCenterTable} className="money-cell">Ingresos Facturados</th>
                        <th style={styleCenterTable} className="money-cell">Diferencia Total-Facturado</th>
                        <th style={styleCenterTable} className="text-center">Detalles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {weeklyCashCuts.data?.results?.length > 0 ? (
                        weeklyCashCuts.data.results.map((item) => (
                          <CashCutItemList key={item.id} item={item} variant="daily" onView={openDetails} />
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="text-center">
                            No hay datos
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                  <TablePaginationFooter
                    totalCount={weeklyCashCuts.data?.count || 0}
                    currentPage={weeklyPage}
                    pageSize={PAGE_SIZE}
                    itemLabel="cortes"
                    isLoading={weeklyCashCuts.isLoading}
                    isFetching={weeklyCashCuts.isFetching}
                    onChangePage={(nextPage) => handlePageChange(setWeeklyPage, nextPage, Math.ceil((weeklyCashCuts.data?.count || 0) / PAGE_SIZE))}
                  />
                </div>
              )}
            </TabPane>
            <TabPane tabId="mensual">
              <div style={{ marginTop: "20px", marginBottom: "20px" }}></div>
              <ListHeader
                handleOnChangeSearch={handleChangeSearch}
                handleSearch={handleSearch}
                searchTxt={searchTxt}
                filtersContent={
                  <div className="cash-cut-filters">
                    <div className="cash-cut-filters-date">
                      <ListFilterBar
                        dateFilter={dateFilter}
                        onDateFilterChange={handleDateFilterChange}
                      />
                    </div>
                    <div className="cash-cut-filters-actions">
                      <ListFilterBar
                        selects={[
                          {
                            name: "monthlyType",
                            label: "Tipo de corte",
                            icon: "fa fa-cash-register",
                            value: monthlyType,
                            onChange: handleMonthlyTypeChange,
                            options: monthlyTypeOptions,
                          },
                        ]}
                        onClear={clearListFilters}
                      />
                    </div>
                  </div>
                }
              />
              {!monthlyCashCuts.error && monthlyCashCuts.isLoading ? (
                <div className="my-5 text-center">
                  <Loading isLoading={monthlyCashCuts.isLoading} size={2} />
                </div>
              ) : monthlyCashCuts.error && !monthlyCashCuts.isLoading ? (
                <div className="my-5 text-center">
                  <h1>Error intenta mas tarde</h1>
                </div>
              ) : (
                <div style={{ marginTop: "30px" }} className="table-responsive">
                  <Table className="table-hover-effect table-interactive">
                    <thead>
                      <tr>
                        <th style={styleCenterTable} className="text-cell">Tipo</th>
                        <th style={styleCenterTable} className="text-cell">Fecha</th>
                        <th style={styleCenterTable} className="money-cell">Ingresos Totales</th>
                        <th style={styleCenterTable} className="money-cell">Ingresos Facturados</th>
                        <th style={styleCenterTable} className="money-cell">Diferencia Total-Facturado</th>
                        <th style={styleCenterTable} className="text-center">Detalles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyCashCuts.data?.results?.length > 0 ? (
                        monthlyCashCuts.data.results.map((item) => (
                          <CashCutItemList key={item.id} item={item} variant="daily" onView={openDetails} />
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="text-center">
                            No hay datos
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                  <TablePaginationFooter
                    totalCount={monthlyCashCuts.data?.count || 0}
                    currentPage={monthlyPage}
                    pageSize={PAGE_SIZE}
                    itemLabel="cortes"
                    isLoading={monthlyCashCuts.isLoading}
                    isFetching={monthlyCashCuts.isFetching}
                    onChangePage={(nextPage) => handlePageChange(setMonthlyPage, nextPage, Math.ceil((monthlyCashCuts.data?.count || 0) / PAGE_SIZE))}
                  />
                </div>
              )}
            </TabPane>
          </TabContent>
        </div>
        <CashCutDetailsModal
          isOpen={detailsModalOpen}
          toggle={closeDetails}
          item={selectedCashCut}
          businessInfo={businessInfo.data?.results?.[0]}
          onPrint={handleBeforePrint}
        />
        <CashCutTicketPrinter
          item={cashCutToPrint.item}
          businessInfo={businessInfo.data?.results?.[0]}
          autoPrint={cashCutToPrint.isReady}
          onAfterPrint={handleAfterPrint}
        />
      </Container>
    </div>
  )
}

export default CashCutList
